import { useMemo } from "react";
import { ArrowLeft, Share2, Shield } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { cn } from "@/lib/utils";
import { useTokenValidation } from "@/hooks/useTokenValidation";
import { UserProfileMenu } from "./UserProfileMenu";
import sirppiLogo from "@/assets/sirppi-logo.svg";

// Helper function to determine branding text based on route
const getMobileBrandingText = (pathname: string): string => {
  // Auth route uses Sirppi branding
  if (pathname === '/login') {
    return 'Sirppi.ai';
  }
  
  // Profile and Settings use Sirppi branding (platform-level pages)
  if (pathname === '/profile' || pathname === '/settings' || 
      pathname.startsWith('/profile/') || pathname.startsWith('/settings/')) {
    return 'Sirppi.ai';
  }
  
  // Marketing pages use Sirppi branding
  if (pathname === '/' || pathname === '/products' || pathname === '/about' || 
      pathname === '/contact' || pathname === '/pricing' ||
      pathname === '/terms' || pathname === '/privacy' || pathname === '/refund' || 
      pathname === '/cancellation') {
    return 'Sirppi.ai';
  }
  
  // Studio pages
  if (pathname.startsWith('/studio')) {
    return 'Career Studio';
  }
  
  // Default to Career Path for app pages
  return 'Career Path';
};

interface MobileHeaderProps {
  title: string;
  showBack?: boolean;
  showShare?: boolean;
  onShare?: () => void;
  className?: string;
}

export const MobileHeader = ({ 
  title, 
  showBack = false, 
  showShare = false,
  onShare,
  className 
}: MobileHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { result } = useTokenValidation();
  
  const isAdminView = location.pathname.startsWith('/admin');
  
  // Get dynamic branding based on current route
  const displayTitle = useMemo(() => {
    return title || getMobileBrandingText(location.pathname);
  }, [title, location.pathname]);

  const handleShare = async () => {
    if (onShare) {
      onShare();
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled or failed');
      }
    }
  };

  return (
    <header className={cn(
      "md:hidden sticky top-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border safe-top",
      className
    )}>
      <div className="flex items-center justify-between h-14 px-3 md:px-4">
        {/* Left - Back button or empty space */}
        <div className="w-9 md:w-10 flex-shrink-0">
          {showBack && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-9 w-9 md:h-10 md:w-10"
              aria-label="Go back"
            >
              <ArrowLeft className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
          )}
        </div>
        
        {/* Center - Title (dynamic branding) */}
        <div className="flex-1 min-w-0 px-2 relative group">
          <div className="flex items-center justify-center gap-2">
            <img src={sirppiLogo} alt="Sirppi" className="h-7 w-auto object-contain flex-shrink-0" />
            <h1 className="text-sm md:text-base font-semibold text-foreground truncate text-center leading-none">
              {displayTitle}
            </h1>
            {result?.isAdmin && (
              <Badge variant="default" className="bg-primary text-primary-foreground px-1.5 py-0.5 text-[10px] h-5 flex items-center gap-0.5">
                <Shield className="w-2.5 h-2.5" />
                <span className="leading-none">Admin</span>
              </Badge>
            )}
          </div>
        </div>
        
        {/* Right - Sign In / Profile */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {showShare && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              className="h-9 w-9 md:h-10 md:w-10"
              aria-label="Share"
            >
              <Share2 className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
          )}
          
          <div className="scale-90">
            <UserProfileMenu />
          </div>
        </div>
      </div>
    </header>
  );
};
